/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { fileService } from 'src/core/services/api/fileService';
import { httpService } from 'src/core/services/api/httpService';
import { FileType } from 'src/core/types/FileType';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { appVersionSwaggerToAppVersionStore } from 'src/luis/modules/apps/handlers/luisAppMappers';
import { AppVersionSettings } from 'src/luis/projects/apps/types/AppVersionSettings';
import { AppVersionSwagger } from 'src/luis/projects/apps/types/swagger/AppVersionSwagger';
import { LuisId } from 'src/luis/types/Resource';
import { AppVersionStore } from 'src/luis/types/stores/apps/AppVersionStore';

/**
 * Represents a service for manipulation of apps.
 */
export const getAppVersionService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment({
        apiVersion: 'v3.0-preview'
    });
    const { webApiUrl } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url scheme for this service.
     *
     * @param appVersionId An optional version id to add.
     * @returns A url string to call.
     */
    const getUrlBuilder = (appVersionId?: LuisId) =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .resource('versions', appVersionId);

    /**
     * Gets a list of app versions.
     *
     * @returns A promise with the app versions.
     */
    const getAppVersions = async (): Promise<AppVersionStore[]> => {
        const requestUrl = getUrlBuilder().build();
        const { data: appVersionItems } = await httpService.get<AppVersionSwagger[]>(requestUrl, apiOptions);

        return appVersionItems.map(appVersionSwaggerToAppVersionStore);
    };

    /**
     * Starts the chunking process to get the apps.
     *
     * @param handler The handler to used to fetch the apps.
     */
    const getAppVersionsByChunking = async (handler: OperationHandler<AppVersionSwagger[]>, pageSize: number): Promise<void> => {
        const urlGenerator = (trial: number) =>
            getUrlBuilder()
                .queryParam('skip', `${trial * pageSize}`)
                .queryParam('take', `${pageSize}`)
                .build();

        await createOperationCall(urlGenerator, apiOptions, handler);
    };

    /**
     * Gets an app version by id.
     *
     * Note: Backslash sent intentionally due to an issue with ASP.Net
     * identifying path as a directory if the last token contains a
     * '.' character.
     *
     * @param param0.id The id of the app version to fetch.
     * @returns A promise with the fetched app version.
     */
    const getAppVersionById = async ({ id }: Pick<AppVersionStore, 'id'>): Promise<AppVersionStore> => {
        const requestUrl = getUrlBuilder(`${id}/`).build();
        const { data: appVersionItem } = await httpService.get<AppVersionSwagger>(requestUrl, apiOptions);

        return appVersionSwaggerToAppVersionStore(appVersionItem);
    };

    /**
     * Activates an app version by id.
     *
     * @param param0.id The id of the app version to fetch.
     * @returns A promise with the fetched app version.
     */
    const activateAppVersion = async ({ id }: Pick<AppVersionStore, 'id'>): Promise<void> => {
        const requestUrl = urlBuilder()
            .baseUrl(webApiUrl)
            .app(context.appId)
            .resource('activeVersion')
            .build();

        await httpService.put<void>(requestUrl, JSON.stringify(id), apiOptions);
    };

    /**
     * Exports an app version by id.
     *
     * @param param0.id The id of the app version to export.
     */
    const exportAppVersion = async ({
        appName,
        versionId,
        fileType
    }: {
        appName: string;
        versionId: string;
        fileType: FileType;
    }): Promise<void> => {
        let url: string;
        let fileName: string;

        const options = luisRequestOptionsBuilder()
            .useProperServiceKey(context.serviceKey, isProduction)
            .useFileContent(fileType)
            .build();

        if (fileType === 'gz') {
            url = urlBuilder()
                .baseUrl(webApiUrl)
                .resource('package', context.appId)
                .resource('versions', versionId)
                .resource('gzip')
                .build();

            fileName = `${context.appId}_v${versionId}.${fileType}`;
        } else {
            url = getUrlBuilder(versionId)
                .resource('export')
                .queryParam('format', fileType)
                .build();

            fileName = `${appName}.${fileType}`;
        }

        await fileService.download(url, fileName, options);
    };

    /**
     * Imports an app version.
     *
     * @param param0.id An optional id to upload the version with.
     * @param param0.fileData Valid app version data to import.
     * @param param0.fileType The file type to import.
     * @returns A promise with the new id of the imported app version.
     */
    const importAppVersion = async ({
        id,
        fileData,
        fileType
    }: Partial<Pick<AppVersionStore, 'id'>> & { fileData: string; fileType: FileType }): Promise<string> => {
        const requestUrl = urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .resource('versions')
            .resource('import')
            .queryParam('versionId', id)
            .build();

        const options = luisRequestOptionsBuilder()
            .useProperServiceKey(context.serviceKey, isProduction)
            .useFileContent(fileType)
            .build();

        const { data: appVersionId } = await fileService.upload<string>(requestUrl, fileData, options);

        return appVersionId;
    };

    /**
     * Renames an app version.
     *
     * Note: Backslash sent intentionally due to an issue with ASP.Net
     * identifying path as a directory if the last token contains a
     * '.' character.
     *
     * @param param0.id The id The id of the app version to rename.
     * @param param0.version New version name.
     */
    const renameAppVersion = async ({ id, version }: Pick<AppVersionStore, 'id'> & { version: string }): Promise<void> => {
        const requestUrl = getUrlBuilder(`${id}/`).build();

        await httpService.put<string>(requestUrl, { version }, apiOptions);
    };

    /**
     * Clones an app version.
     *
     * @param param0.id The id The id of the app version to rename.
     * @param param0.version New version name.
     */
    const cloneAppVersion = async ({ id, version }: Pick<AppVersionStore, 'id'> & { version: string }): Promise<void> => {
        const requestUrl = getUrlBuilder(id)
            .resource('clone')
            .build();

        await httpService.post<string>(requestUrl, { version }, apiOptions);
    };

    /**
     * Deletes the app version with the given id.
     *
     * Note: Backslash sent intentionally due to an issue with ASP.Net
     * identifying path as a directory if the last token contains a
     * '.' character.
     *
     * @param param0.id The id of the app version to be deleted.
     */
    const deleteAppVersion = async ({ id }: Pick<AppVersionStore, 'id'>): Promise<void> => {
        const requestUrl = getUrlBuilder(`${id}/`).build();

        await httpService.delete(requestUrl, apiOptions);
    };

    /**
     * Gets an app version settings by id.
     *
     * @param param0.id The id of the app version to fetch.
     * @returns A promise with the fetched app version.
     */
    const getAppVersionSettingsById = async ({ id }: Pick<AppVersionStore, 'id'>): Promise<AppVersionSettings> => {
        const requestUrl = getUrlBuilder(id)
            .resource('settings')
            .build();

        const { data: appVersionSettings } = await httpService.get<AppVersionSettings>(requestUrl, apiOptions);

        return appVersionSettings;
    };

    /**
     * Updates an app version settings.
     *
     * @param param0.id The id of the app version to update settings for.
     * @param param0.settings The new settings for the app version.
     */
    const updateAppVersionSettings = async ({ id, settings }: Pick<AppVersionStore, 'id' | 'settings'>): Promise<void> => {
        const requestUrl = getUrlBuilder(id)
            .resource('settings')
            .build();

        await httpService.put<AppVersionSettings>(requestUrl, settings, apiOptions);
    };

    return {
        getAppVersions,
        getAppVersionsByChunking,
        getAppVersionById,
        exportAppVersion,
        importAppVersionByVersion: importAppVersion,
        renameAppVersion,
        cloneAppVersion,
        deleteAppVersion,
        getAppVersionSettingsById,
        updateAppVersionSettings,
        activateAppVersion
    };
});
